import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumb } from "@/components/Breadcrumb";
import { StructuredData } from "@/components/StructuredData";
import { Card } from "@/components/ui/card";
import { Shield, Zap, Users, Heart } from "lucide-react";
import aboutImage from "@/assets/about-hero.jpg";
import teamMember1 from "@/assets/team/member-1.jpg";
import teamMember2 from "@/assets/team/member-2.jpg";
import teamMember3 from "@/assets/team/member-3.jpg";
import teamMember4 from "@/assets/team/member-4.png";

const About = () => {
  const breadcrumbs = [
    { label: "About", href: "/about" }
  ];

  const values = [
    {
      icon: Shield,
      title: "Security First",
      description: "We use cryptographically secure random number generation to ensure fair and unbiased results every time."
    },
    {
      icon: Zap,
      title: "Fast & Reliable",
      description: "Instant random number generation with no delays. Our tools are optimized for speed and always available."
    },
    {
      icon: Users,
      title: "User Focused",
      description: "Built with simplicity in mind. No complicated settings, no learning curve - just pure functionality."
    },
    {
      icon: Heart,
      title: "Free Forever",
      description: "We believe quality random number tools should be accessible to everyone, completely free of charge."
    }
  ];

  return (
    <Layout>
      <SEO 
        title="About Us - Cryptographically Secure Random Number Generator"
        description="Learn about our mission to provide free, cryptographically secure random number generation for everyone. Trusted by thousands for fair lottery draws and games."
        canonical="https://numbers-generator.com/about"
        keywords="about random number generator, cryptographic RNG, secure random numbers, fair lottery"
      />
      <StructuredData 
        type="BreadcrumbList"
        breadcrumbs={[
          { name: "Home", url: "https://numbers-generator.com/" },
          { name: "About", url: "https://numbers-generator.com/about" }
        ]}
      />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Breadcrumb items={breadcrumbs} />
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            RandomGen Numbers Generator - About Us
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Your trusted source for cryptographically secure random number generation - learn how our 
            free random number generator delivers truly random results
          </p>
        </header>

        <div className="space-y-8">
          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              RandomGen was created to provide everyone with access to a high-quality, truly random number generator. 
              Whether you're running a lottery, playing games, conducting research, or making decisions, you deserve 
              random number results you can trust. Our random number generator uses cryptographically secure algorithms 
              to ensure fair and unpredictable results every time.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              We leverage modern cryptographic APIs to deliver randomness that's not just good enough, but 
              cryptographically secure. Unlike basic random number generators that use predictable pseudo-random algorithms, 
              our random number generator produces true randomness with no patterns to exploit - just pure, unbiased random numbers.
            </p>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <Card key={value.title} className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                      <p className="text-muted-foreground">{value.description}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <Card className="p-8 bg-gradient-to-r from-primary/10 to-primary-glow/10">
            <h2 className="text-2xl font-semibold mb-4">Why Choose RandomGen?</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span><strong>Cryptographic Security:</strong> Military-grade randomness using Web Crypto API</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span><strong>No Registration Required:</strong> Use all features immediately without signing up</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span><strong>Completely Free:</strong> No premium features, no hidden costs, no ads</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span><strong>Privacy Focused:</strong> We don't store your generated numbers or personal data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span><strong>Mobile Friendly:</strong> Works perfectly on any device, anywhere</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span><strong>Always Available:</strong> 24/7 uptime with instant generation</span>
              </li>
            </ul>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Our Technology</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              RandomGen is built on modern web technologies and follows industry best practices for random number 
              generation. We use the Web Crypto API's <code className="bg-muted px-2 py-1 rounded">crypto.getRandomValues()</code> 
              function, which provides cryptographically strong random values.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              This is the same technology used by security applications, encryption systems, and other scenarios where 
              unpredictable randomness is critical. Every number we generate passes through rigorous randomness tests 
              to ensure uniform distribution and complete unpredictability.
            </p>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-6 text-center">Meet Our Team</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="mb-4 rounded-xl overflow-hidden shadow-lg mx-auto max-w-[250px]">
                  <img 
                    src={teamMember1} 
                    alt="Jason Brantley - Chief Technology Officer and Cryptography Expert specializing in secure random number generation"
                    className="w-full h-auto object-cover aspect-square"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-semibold text-lg">Jason Brantley</h3>
                <p className="text-sm text-muted-foreground">Chief Technology Officer</p>
                <p className="text-xs text-muted-foreground">Cryptography Expert</p>
              </div>

              <div className="text-center">
                <div className="mb-4 rounded-xl overflow-hidden shadow-lg mx-auto max-w-[250px]">
                  <img 
                    src={teamMember2} 
                    alt="Julien Le Vély - Lead Developer and Full Stack Engineer specializing in secure web applications"
                    className="w-full h-auto object-cover aspect-square"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-semibold text-lg">Julien Le Vély</h3>
                <p className="text-sm text-muted-foreground">Lead Developer</p>
                <p className="text-xs text-muted-foreground">Full Stack Engineer</p>
              </div>

              <div className="text-center">
                <div className="mb-4 rounded-xl overflow-hidden shadow-lg mx-auto max-w-[250px]">
                  <img 
                    src={teamMember3} 
                    alt="Jack Finnie - Product Manager overseeing random number generator features and user experience optimization"
                    className="w-full h-auto object-cover aspect-square"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-semibold text-lg">Jack Finnie</h3>
                <p className="text-sm text-muted-foreground">Product Manager</p>
                <p className="text-xs text-muted-foreground">UX Specialist</p>
              </div>

              <div className="text-center">
                <div className="mb-4 rounded-xl overflow-hidden shadow-lg mx-auto max-w-[250px]">
                  <img 
                    src={teamMember4} 
                    alt="Mariella Batista-Hidalgo - Data Scientist analyzing randomness quality and statistical patterns"
                    className="w-full h-auto object-cover aspect-square"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-semibold text-lg">Mariella Batista-Hidalgo</h3>
                <p className="text-sm text-muted-foreground">Data Scientist</p>
                <p className="text-xs text-muted-foreground">Statistics Analyst</p>
              </div>
            </div>
          </Card>
        </div>

        {/* About Hero Image at Bottom */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={aboutImage} 
            alt="Professional office environment showing team collaboration and trust - RandomGen team building cryptographically secure random number generation technology"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default About;
